import { useState } from "react";
import { toast } from "sonner";
import { Copy, Pencil, KeyRound, Send, Trash2, BookOpen, Unplug, PlugZap } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { fmtDateTime } from "../../lib/helpers";
import { copyText, ConnBadge, PreviewWarning } from "./leadUtils";
import { LeadSetupHelp } from "./LeadSetupHelp";

export function LeadSourceCard({ s, team, onEdit, onChanged, onKey }) {
  const { t, lang } = useLang();
  const [help, setHelp] = useState(false);
  const assignee = team.find((m) => m.id === s.assign_to);
  const on = s.status !== "disconnected";
  const call = async (fn, ok) => { try { const r = await fn(); if (ok) toast.success(ok); onChanged(); return r?.data; } catch (e) { toast.error(errMsg(e)); } };
  const ask = (m) => window.confirm(m);
  const disconnect = () => ask(t("Disconnect? The key is revoked immediately and new leads from this source are rejected. Leads already received stay in your CRM.", "¿Desconectar? La clave se revoca de inmediato y se rechazan nuevos prospectos de esta fuente. Los ya recibidos se quedan en su CRM."))
    && call(() => api.post(`/lead-sources/${s.id}/disconnect`), t("Disconnected", "Desconectado"));
  const withKey = async (path, msg) => { if (!ask(msg)) return; const d = await call(() => api.post(`/lead-sources/${s.id}/${path}`)); if (d) onKey(d); };
  const reconnect = () => withKey("reconnect", t("Reconnect with a new key? Paste the new key in Google Ads / your website.", "¿Reconectar con una nueva clave? Pegue la nueva clave en Google Ads / su sitio web."));
  const rotate = () => withKey("rotate-key", t("Generate a new key? The old key stops working immediately.", "¿Generar una nueva clave? La anterior deja de funcionar de inmediato."));
  const test = () => call(() => api.post(`/lead-sources/${s.id}/test`), t("Test lead received", "Prospecto de prueba recibido"));
  const remove = () => ask(t(`Remove "${s.name}"? Leads already received stay in your CRM.`, `¿Quitar "${s.name}"? Los prospectos recibidos se quedan en su CRM.`)) && call(() => api.delete(`/lead-sources/${s.id}`), t("Source removed", "Fuente quitada"));
  const B = ({ id, onClick, Icon, children, cls = "btn-outline" }) => <button className={`${cls} btn-sm`} onClick={onClick} data-testid={`lead-source-${id}-${s.id}`}><Icon className="w-4 h-4" /> {children}</button>;
  return (
    <div className="border-t p-4 grid gap-3" data-testid={`lead-source-${s.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <b className="flex-1 min-w-0 truncate" data-testid={`lead-source-name-${s.id}`}>{s.name}</b>
        <ConnBadge status={s.status} lang={lang} testid={`lead-source-status-${s.id}`} />
      </div>
      {on && s.is_preview && <PreviewWarning t={t} />}
      {on && (
        <div className="flex gap-2 items-center">
          <input className="inp mono text-xs h-10" readOnly value={s.webhook_url} data-testid={`lead-source-url-${s.id}`} />
          <button className="btn-outline btn-sm" onClick={() => copyText(s.webhook_url, t)} data-testid={`lead-source-copy-${s.id}`}><Copy className="w-4 h-4" /></button>
        </div>
      )}
      <div className="text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
        {on && <span>{t("Key", "Clave")}: <span className="mono">lk_…{s.key_hint}</span></span>}
        <span data-testid={`lead-source-count-${s.id}`}>{s.lead_count || 0} {t("leads", "prospectos")}</span>
        <span>{t("Last lead", "Último")}: {s.last_lead_at ? fmtDateTime(s.last_lead_at, lang) : "—"}</span>
        {assignee && <span>{t("Assigned to", "Asignado a")}: {assignee.name}</span>}
        {s.status === "error" && <span className="text-red-600">{s.last_error}</span>}
      </div>
      <div className="flex flex-wrap gap-2">
        {on ? <>
          <B id="help" onClick={() => setHelp(!help)} Icon={BookOpen}>{t("Setup instructions", "Instrucciones")}</B>
          <B id="test" onClick={test} Icon={Send}>{t("Send test lead", "Enviar prueba")}</B>
          <B id="rotate" onClick={rotate} Icon={KeyRound}>{t("New key", "Nueva clave")}</B>
          <B id="disconnect" onClick={disconnect} Icon={Unplug} cls="btn-outline text-red-600">{t("Disconnect", "Desconectar")}</B>
        </> : <B id="reconnect" onClick={reconnect} Icon={PlugZap} cls="btn-primary">{t("Reconnect", "Reconectar")}</B>}
        <B id="edit" onClick={onEdit} Icon={Pencil}>{t("Edit", "Editar")}</B>
        <B id="delete" onClick={remove} Icon={Trash2} cls="btn-ghost text-red-600">{t("Remove", "Quitar")}</B>
      </div>
      {help && on && <LeadSetupHelp source={s} />}
    </div>
  );
}
